import { createFileRoute } from "@tanstack/react-router"
import { formatDate } from "~/utils/datetime"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import NotFound from "~/components/reusable/NotFound"
import { Module, WorkspacePage } from "~/components/design-system/pages"
import { VendorSettingsDialog } from "~/components/vendor-management/VendorSettingsDialog"
import { VendorCapacityTable } from "~/components/vendor-management/VendorCapacityTable"
import { VendorClosingPeriodsCard } from "~/components/vendor-management/VendorClosingPeriodsCard"
import { api } from "~/utils/api"
import { useRecordRecentlyViewed } from "~/hooks/useRecentlyViewed"

export const Route = createFileRoute("/_auth/procurement/vendor-management/$vendorId")({
  component: RouteComponent,
})

function RouteComponent() {
  const { vendorId } = Route.useParams()
  const { data: vendors, isLoading: vendorsLoading } = api.vendor.getActive.useQuery()
  const { data: settings, isLoading: settingsLoading } = api.vendor.getProcurementSettings.useQuery(
    { vendorId },
  )
  const { data: orders = [], isLoading: ordersLoading } = api.vendor.getMonthlyOrders.useQuery(
    { vendorId },
    { staleTime: 0, refetchOnWindowFocus: true },
  )

  const vendor = vendors?.find((v) => v.vendorId === vendorId)
  useRecordRecentlyViewed(
    vendor
      ? {
          type: "vendor",
          route: `/procurement/vendor-management/${vendorId}`,
          label: vendor.name,
        }
      : null,
  )

  if (vendorsLoading || settingsLoading || ordersLoading) return <LoadingOverlay />

  if (!vendor) return <NotFound />

  return (
    <Module
      breadcrumb={
        <BreadcrumbWrapper
          items={[
            { label: "Procurement", href: "/procurement" },
            { label: "Vendor Management", href: "/procurement/vendor-management" },
            { label: vendor.name },
          ]}
        />
      }
      actions={
        <div className="flex items-center gap-3">
          {settings?.settingsUpdatedAt && (
            <span className="text-xs text-fg-subtle">
              Last updated {formatDate(new Date(settings.settingsUpdatedAt))}
            </span>
          )}
          <VendorSettingsDialog
            vendorId={vendorId}
            vendorName={vendor.name}
            settings={settings ?? null}
          />
        </div>
      }
    >
      <WorkspacePage
        inspector={<VendorClosingPeriodsCard vendorId={vendorId} settings={settings ?? null} />}
        inspectorDefaultOpen
        inspectorWidth="lg"
      >
        <VendorCapacityTable orders={orders} settings={settings ?? null} />
      </WorkspacePage>
    </Module>
  )
}
